import styled from 'styled-components';

import { COLORS, MOBILE, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';
import { fadeInWhenReady } from '@/nova/theme/mixins';

import { Text } from '@/nova/components/Text/Text';

import { NewTile as VideoNewTile, SavedBtn } from '../VideoLibrary/VideoLibrary.styles';

/* The ad library shell (Image Banners, Mini-games) reuses the Video Ads library's grid, cards, archived sheet and
   confirm sheet, so the three libraries read as one system. Only the pieces the video library has no equivalent
   for are defined here: an image poster, its placeholder, the draft / ready status dot, and the archived thumb. */

export {
  ArchivedActions,
  ArchivedConfirmText,
  ArchivedDeleteBtn,
  ArchivedInfo,
  ArchivedLink,
  ArchivedLinkText,
  ArchivedList,
  ArchivedName,
  ArchivedRow,
  ArchivedThumb,
  Body,
  BusyInner,
  BusyOverlay,
  BusyText,
  CardProgressFill,
  CardProgressTrack,
  ConfirmBody,
  ConfirmFoot,
  DangerBtn,
  DangerBtnText,
  EmptyNote,
  Intro,
  IntroHead,
  NewTileText,
  Poster,
  PosterEmpty,
  SavedActions,
  SavedBody,
  SavedBtn,
  SavedCard,
  SavedFoot,
  SavedGrid,
  SavedMeta,
  SavedName,
  StatusPill,
  StatusPillText,
  Title,
} from '../VideoLibrary/VideoLibrary.styles';

/** The ad's rendered art, contained in the tall poster frame: the render carries baked text and a logo, so it is
 *  letterboxed rather than cropped. Fades in once loaded where it is given `revealOnLoad`. */
export const PosterImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  ${fadeInWhenReady}
`;

/** Nothing rendered yet: the Lumio mark over a short line, in place of a (misleading) thumbnail. */
export const PosterPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[8]};

  /* A phone row's small poster. */
  ${MOBILE} {
    gap: ${SPACE[4]};
  }
`;

/** The status chip's dot: draft (grey) or ready (green). */
export const StatusDot = styled.span<{ $tone: 'draft' | 'ready' }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${(p) => (p.$tone === 'ready' ? STATUS_COLORS.positive : COLORS.textFaint)};
`;

/** An archived row's thumbnail image. */
export const ArchivedThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

/** A card action that can be unavailable (nothing generated to download yet) or busy (a long server export being
 *  prepared): dimmed and inert while disabled. */
export const ActionBtn = styled(SavedBtn)`
  &:disabled {
    opacity: 0.4;
    cursor: default;
    background: transparent;
    color: ${COLORS.textMuted};
  }
`;

/** The "create new" tile; disabled (with a note under its label) when there is nothing to create from yet. */
export const NewTile = styled(VideoNewTile)`
  &:disabled {
    cursor: default;
    color: ${COLORS.textFaint};
    border-color: ${COLORS.hairline};
  }
`;

/** Why the "create new" tile is disabled (e.g. the brand has no promotion yet). */
export const NewTileNote = styled(Text).attrs({ tag: 'span' as const })`
  max-width: 220px;
  padding: 0 ${SPACE[12]};
  font-size: 12px;
  line-height: 1.45;
  text-align: center;
  color: ${COLORS.textFaint};

  /* A phone's "create new" row: the note on its own line under the icon and label. */
  ${MOBILE} {
    flex-basis: 100%;
    max-width: none;
    padding: 0;
  }
`;
